import React, { useState } from "react";

// Formatting helper (mirrors prior MUI approach but adapted for Tailwind)
const normalizeNumericLike = (value) => {
  if (value && typeof value === 'object') {
    if (typeof value.$numberDouble !== 'undefined') {
      const n = Number(value.$numberDouble);
      return Number.isFinite(n) ? n : null;
    }
    if (typeof value.$numberInt !== 'undefined') {
      const n = Number(value.$numberInt);
      return Number.isFinite(n) ? n : null;
    }
    if (typeof value.$numberLong !== 'undefined') {
      const n = Number(value.$numberLong);
      return Number.isFinite(n) ? n : null;
    }
  }
  return value;
};

const formatValue = (value, key) => {
  value = normalizeNumericLike(value);
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'number' && !Number.isFinite(value)) return '-';
  if (key === 'count') return Number(value).toFixed(0);
  if (typeof value === 'number') {
    const abs = Math.abs(value);
    if (abs !== 0 && abs < 0.01) return value.toFixed(4);
    return value.toFixed(2);
  }
  return String(value);
};

const TableShell = ({ children }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      {children}
    </table>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-2 py-1 text-left font-semibold border-b dark:border-gray-800 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-2 py-1 border-b dark:border-gray-900 align-top ${className}`}>{children}</td>
);

function AiComment({ text }) {
  if (!text) return null;
  return (
    <div className="whitespace-pre-wrap text-[13px] leading-6 text-gray-700 dark:text-gray-300">{text}</div>
  );
}

function ConsStats({ data }) {
  if (!data) return null;
  const { consolidation_score, bars_analyzed, criteria_met, div_details } = data || {};
  const prettyKey = (k) =>
    k
      ? k
          .split('_')
          .map(part => {
            const p = String(part).toLowerCase();
            if (p === 'bb' || p === 'atr' || p === 'ma') return p.toUpperCase();
            return part.charAt(0).toUpperCase() + part.slice(1);
          })
          .join(' ')
      : '';
  const prettyDivKeyMap = {
    total_reg_bullish: 'Total Regular Bullish',
    total_reg_bearish: 'Total Regular Bearish',
    total_hid_bullish: 'Total Hidden Bullish',
    total_hid_bearish: 'Total Hidden Bearish',
    knox_bullish_count: 'Knox Bullish Count',
    knox_bearish_count: 'Knox Bearish Count',
    last_div_type: 'Last Divergence Type',
    bullish_div_score: 'Bullish Divergence Score',
  };
  const prettyDivKey = (k) => prettyDivKeyMap[k] || prettyKey(k);
  return (
    <div className="space-y-3">
      <div className="text-sm text-left">
        Consolidation Score: <span className="font-semibold">{typeof consolidation_score !== 'undefined' ? Number(consolidation_score).toFixed(2) : '-'}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {criteria_met && (
          <div>
            <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Criteria Analysis</div>
            <div className="overflow-x-auto">
              <table className="w-auto mx-auto text-sm">
                <tbody>
                  {Object.entries(criteria_met).map(([k, v]) => (
                    <tr key={k}>
                      <Td className="whitespace-nowrap text-right pr-2 w-28 sm:w-36">{prettyKey(k)}:</Td>
                      <Td className="align-middle text-left">
                        {v ? (
                          <span className="inline-flex items-center text-green-600 dark:text-green-400">
                            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-red-600 dark:text-red-400">
                            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-2.293-9.707a1 1 0 011.414 0L10 8.586l.879-.879a1 1 0 111.414 1.414L11.414 10l.879.879a1 1 0 01-1.414 1.414L10 11.414l-.879.879a1 1 0 01-1.414-1.414L8.586 10l-.879-.879a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {div_details && (
          <div>
            <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Divergence Details</div>
            <TableShell>
              <tbody>
                {Object.entries(div_details).map(([k, v]) => (
                  <tr key={k}>
                    <Td className="whitespace-nowrap text-right pr-2">{prettyDivKey(k)}:</Td>
                    <Td className={typeof v === 'number' ? 'text-center' : ''}>{typeof v === 'number' ? Number(v).toFixed(2) : String(v)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </div>
        )}
      </div>

      {typeof bars_analyzed !== 'undefined' && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400">Note: Analysis based on up to {bars_analyzed} bars.</div>
      )}
    </div>
  );
}

function DivStats({ data }) {
  if (!data) return null;
  const tabs = [
    { key: 'regular', label: 'Regular' },
    { key: 'hidden', label: 'Hidden' },
    { key: 'regular_and_hidden', label: 'Regular + Hidden' },
    { key: 'knox', label: 'Knoxville' },
  ];
  const [active, setActive] = useState('regular');
  const metricCols = [
    { label: 'Count', key: 'count' },
    { label: 'Avg % Change 1d', key: 'avg_price_change_pct_1d' },
    { label: 'Avg % Change 2d', key: 'avg_price_change_pct_2d' },
    { label: 'Increase Ratio 1d', key: 'price_increase_ratio_1d' },
    { label: 'Increase Ratio 2d', key: 'price_increase_ratio_2d' },
  ];
  const currentGroup = data[active] || {};
  const trends = Object.keys(currentGroup);
  const sides = ['bullish', 'bearish'];
  const pctColor = (v) => v > 0 ? 'text-green-600 dark:text-green-400' : v < 0 ? 'text-red-600 dark:text-red-400' : '';
  return (
    <div className="space-y-4">
      {data.current_trend && (() => {
        const ct = data.current_trend;
        // New shape: nested `div` and `knox` dicts. Old shape: flat fields on current_trend.
        const trendRows = (ct.div || ct.knox)
          ? ['div', 'knox']
              .filter((k) => ct[k] && typeof ct[k] === 'object')
              .map((k) => ({
                key: k,
                label: k === 'knox' ? 'Knoxville' : 'Divergence',
                row: ct[k],
              }))
          : [{ key: 'flat', label: ct.divergence_category || 'Divergence', row: ct }];
        return (
          <div>
            <div className="text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500 dark:text-gray-400">Current Trend Summary</div>
            <div className="text-sm mb-2 flex flex-wrap gap-x-6 gap-y-1">
              <span>Data: <span className="font-medium">{ct.date || '-'}</span></span>
              <span>
                Trend: {" "}
                <span className={`font-semibold ${ct.trend === 'UPWARD' ? 'text-green-600 dark:text-green-400' : ct.trend === 'DOWNWARD' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {ct.trend || '-'}
                </span>
              </span>
            </div>
            <TableShell>
              <thead>
                <tr>
                  <Th className="text-center">Divergence category</Th>
                  <Th className="text-center">Divergence Direction</Th>
                  <Th className="text-center">Count</Th>
                  <Th className="text-center">Avg % Change 1d</Th>
                  <Th className="text-center">Avg % Change 2d</Th>
                  <Th className="text-center">Increase Ratio 1d</Th>
                  <Th className="text-center">Increase Ratio 2d</Th>
                </tr>
              </thead>
              <tbody>
                {trendRows.map(({ key, label, row }) => (
                  <tr key={key}>
                    <Td className="text-center">{row.divergence_category || '-'}</Td>
                    <Td className="text-center">{row.divergence_direction || '-'}</Td>
                    <Td className="text-center">{formatValue(row.count, 'count')}</Td>
                    <Td className="text-center"><span className={typeof row.avg_price_change_pct_1d === 'number' ? pctColor(row.avg_price_change_pct_1d) : ''}>{formatValue(row.avg_price_change_pct_1d, '')}{typeof row.avg_price_change_pct_1d === 'number' ? '%' : ''}</span></Td>
                    <Td className="text-center"><span className={typeof row.avg_price_change_pct_2d === 'number' ? pctColor(row.avg_price_change_pct_2d) : ''}>{formatValue(row.avg_price_change_pct_2d, '')}{typeof row.avg_price_change_pct_2d === 'number' ? '%' : ''}</span></Td>
                    <Td className="text-center">{formatValue(row.price_increase_ratio_1d, '')}</Td>
                    <Td className="text-center">{formatValue(row.price_increase_ratio_2d, '')}</Td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </div>
        );
      })()}
    <div className="text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500 dark:text-gray-400">Historical Divergence Statistics</div>
    <div className="flex gap-2 text-sm font-medium border-b dark:border-gray-700">
      {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-3 py-1 rounded-t-md border border-b-0 dark:border-gray-700 ${active === t.key ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-50' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >{t.label}</button>
        ))}
      </div>
      <TableShell>
        <thead>
          <tr>
            <Th className="text-center">Trend</Th>
            <Th className="text-center">Divergence Direction</Th>
            {metricCols.map(c => <Th key={c.key} className="text-center">{c.label}</Th>)}
          </tr>
        </thead>
        <tbody>
          {trends.flatMap(tr => sides.map(side => ({ tr, side }))).map(({ tr, side }) => {
            const base = currentGroup[tr];
            const row = base && base[side];
            if (!row) return null;
            return (
              <tr key={tr + side}>
                <Td className="whitespace-nowrap">{tr}</Td>
                <Td className="capitalize">{side}</Td>
                {metricCols.map(mc => {
                  const v = row[mc.key];
                  if (mc.key.startsWith('avg_price_change_pct')) {
                    return <Td key={mc.key} className="text-center"><span className={typeof v === 'number' ? pctColor(v) : ''}>{formatValue(v, mc.key)}{typeof v === 'number' ? '%' : ''}</span></Td>;
                  }
                  return <Td key={mc.key} className="text-center">{formatValue(v, mc.key)}</Td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </TableShell>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">Note: Analysis based on up to {data.max_stats_bars_divergence} bars.</div>
    </div>
  );
}

function EnsembleStats({ data }) {
  if (!data) return null;
  const { bullish_score, bearish_score, conflict_reduction, lookback_days_used, ...rest } = data;
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Model Consensus Score</div>
      <TableShell>
        <thead>
          <tr>
            <Th className="text-center">Bullish Score</Th>
            <Th className="text-center">Bearish Score</Th>
            <Th className="text-center">Conflict Reduction</Th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <Td className="text-center text-green-600 dark:text-green-400">{formatValue(bullish_score, '')}</Td>
            <Td className="text-center text-red-600 dark:text-red-400">{formatValue(bearish_score, '')}</Td>
            <Td className={`text-center ${conflict_reduction > 0 ? 'text-green-600 dark:text-green-400' : conflict_reduction < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{formatValue(conflict_reduction, '')}</Td>
          </tr>
        </tbody>
      </TableShell>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">Note: Scores are derived from multiple AI models and reflect the weighted directional consensus, adjusted for prediction conflicts.</div>
    </div>
  );
}

function ModelStats({ data }) {
  if (!data) return null;
  const {
    max_stats_bars_model,
    available_models,
    best_model_12_months,
    current_signals,
    individual_model_stats,
    model_pair_stats,
  } = data;

  const models = (() => {
    const fromAvailable = Array.isArray(available_models) ? available_models : [];
    const fromSignals = current_signals
      ? ['model1', 'model2', 'model_ensemble'].filter((m) => current_signals[`${m}_signal`])
      : [];
    const fromStats = individual_model_stats ? Object.keys(individual_model_stats) : [];
    return [...new Set([...fromAvailable, ...fromSignals, ...fromStats])].filter(Boolean);
  })();

  const modelLabel = (m) => {
    if (m === 'model_ensemble') return 'Model Ensemble';
    if (m === 'model1') return 'Model1';
    if (m === 'model2') return 'Model2';
    return String(m || '-');
  };

  const parsePairKey = (pairKey) => {
    const known = ['model1', 'model2', 'model_ensemble'];
    for (const left of known) {
      for (const right of known) {
        if (left !== right && pairKey === `${left}_${right}`) {
          return { left, right };
        }
      }
    }
    return null;
  };

  const metricColumns = [
    { label: 'Count', key: 'count' },
    { label: 'Avg % Change 1d', key: 'avg_price_change_pct_1d' },
    { label: 'Avg % Change 2d', key: 'avg_price_change_pct_2d' },
    { label: 'Increase Ratio 1d', key: 'price_increase_ratio_1d' },
    { label: 'Increase Ratio 2d', key: 'price_increase_ratio_2d' },
  ];

  const colorForSignal = (signal) => {
    if (!signal) return '';
    if (signal.includes('UP')) return 'text-green-600 dark:text-green-400';
    if (signal.includes('DOWN')) return 'text-red-600 dark:text-red-400';
    return 'text-amber-600 dark:text-amber-400';
  };

  const signalColor = (v) => (typeof v === 'number' ? (v > 0 ? 'text-green-600 dark:text-green-400' : v < 0 ? 'text-red-600 dark:text-red-400' : '') : '');

  return (
    <div className="space-y-4">
      {(available_models || best_model_12_months) && (
        <div className="text-sm mb-1 text-left">
          <span className="text-gray-600 dark:text-gray-400">Available Models: </span>
          <span className="font-medium">{Array.isArray(available_models) ? available_models.join(', ') : (available_models || '-')}</span>
          {best_model_12_months && (
            <span className="ml-4">
              <span className="text-gray-600 dark:text-gray-400">Best Model (12M): </span>
              <span className="font-semibold text-green-600 dark:text-green-400">{best_model_12_months}</span>
            </span>
          )}
        </div>
      )}

      {current_signals && (
        <div>
          <div className="text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500 dark:text-gray-400">Current Model Signals ({current_signals.date})</div>
          <TableShell>
            <thead>
              <tr>
                <Th className="text-center">Model</Th>
                <Th className="text-center">Signal</Th>
                <Th className="text-center">Count</Th>
                <Th className="text-center">Avg % Change 1d</Th>
                <Th className="text-center">Avg % Change 2d</Th>
                <Th className="text-center">Increase Ratio 1d</Th>
                <Th className="text-center">Increase Ratio 2d</Th>
              </tr>
            </thead>
            <tbody>
              {[
                ...models.map((m) => ({
                  label: modelLabel(m),
                  signal: current_signals[`${m}_signal`],
                  count: current_signals[`${m}_count`],
                  avg1d: current_signals[`${m}_avg_price_change_pct_1d`],
                  avg2d: current_signals[`${m}_avg_price_change_pct_2d`],
                  ratio1d: current_signals[`${m}_price_increase_ratio_1d`],
                  ratio2d: current_signals[`${m}_price_increase_ratio_2d`],
                })),
                {
                  label: 'Model1+Model2',
                  signal: current_signals.model1_signal && current_signals.model2_signal
                    ? `${current_signals.model1_signal}-${current_signals.model2_signal}`
                    : '-',
                  count: current_signals.model1_model2_count,
                  avg1d: current_signals.model1_model2_avg_price_change_pct_1d,
                  avg2d: current_signals.model1_model2_avg_price_change_pct_2d,
                  ratio1d: current_signals.model1_model2_price_increase_ratio_1d,
                  ratio2d: current_signals.model1_model2_price_increase_ratio_2d,
                },
                {
                  label: 'Model1+Ensemble',
                  signal: current_signals.model1_signal && current_signals.model_ensemble_signal
                    ? `${current_signals.model1_signal}-${current_signals.model_ensemble_signal}`
                    : '-',
                  count: current_signals.model1_model_ensemble_count,
                  avg1d: current_signals.model1_model_ensemble_avg_price_change_pct_1d,
                  avg2d: current_signals.model1_model_ensemble_avg_price_change_pct_2d,
                  ratio1d: current_signals.model1_model_ensemble_price_increase_ratio_1d,
                  ratio2d: current_signals.model1_model_ensemble_price_increase_ratio_2d,
                },
                {
                  label: 'Model2+Ensemble',
                  signal: current_signals.model2_signal && current_signals.model_ensemble_signal
                    ? `${current_signals.model2_signal}-${current_signals.model_ensemble_signal}`
                    : '-',
                  count: current_signals.model2_model_ensemble_count,
                  avg1d: current_signals.model2_model_ensemble_avg_price_change_pct_1d,
                  avg2d: current_signals.model2_model_ensemble_avg_price_change_pct_2d,
                  ratio1d: current_signals.model2_model_ensemble_price_increase_ratio_1d,
                  ratio2d: current_signals.model2_model_ensemble_price_increase_ratio_2d,
                },
              ]
                .filter((row) => row.signal || row.count || row.avg1d || row.avg2d || row.ratio1d || row.ratio2d)
                .map((row) => (
                <tr key={row.label}>
                  <Td className="text-center font-medium">{row.label}</Td>
                  <Td className="text-center">
                    {row.label.includes('+') && row.signal && row.signal !== '-' ? (
                      <span className="font-semibold">
                        {row.signal.split('-').map((sig, idx) => (
                          <React.Fragment key={idx}>
                            <span className={colorForSignal(sig)}>{sig}</span>
                            {idx === 0 && <span className="text-gray-500 dark:text-gray-400">-</span>}
                          </React.Fragment>
                        ))}
                      </span>
                    ) : (
                      <span className={`font-semibold ${colorForSignal(row.signal || '')}`}>{row.signal || '-'}</span>
                    )}
                  </Td>
                  <Td className="text-center">{formatValue(row.count, 'count')}</Td>
                      <Td className="text-center"><span className={signalColor(normalizeNumericLike(row.avg1d))}>{formatValue(row.avg1d, '')}{typeof normalizeNumericLike(row.avg1d) === 'number' ? '%' : ''}</span></Td>
                      <Td className="text-center"><span className={signalColor(normalizeNumericLike(row.avg2d))}>{formatValue(row.avg2d, '')}{typeof normalizeNumericLike(row.avg2d) === 'number' ? '%' : ''}</span></Td>
                  <Td className="text-center">{formatValue(row.ratio1d, '')}</Td>
                  <Td className="text-center">{formatValue(row.ratio2d, '')}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </div>
      )}

      {individual_model_stats && (
        <div>
          <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Individual Model Statistics</div>
          <TableShell>
            <thead>
              <tr>
                <Th className="text-center">Model</Th>
                <Th className="text-center">Signal</Th>
                {metricColumns.map(c => <Th key={c.key} className="text-center">{c.label}</Th>)}
              </tr>
            </thead>
            <tbody>
              {models.flatMap((m) => {
                const bucket = individual_model_stats[m] || {};
                return ['UPWARD','DOWNWARD','SIDEWAYS'].map((dir) => {
                  const key = `${m}_${dir}`;
                  const cell = bucket[key];
                  if (!cell) return null;
                  return (
                    <tr key={key}>
                      <Td className="whitespace-nowrap font-medium">{modelLabel(m)}</Td>
                      <Td className={`whitespace-nowrap font-semibold ${colorForSignal(dir)}`}>{dir}</Td>
                      {metricColumns.map(mc => {
                        const v = cell[mc.key];
                        if (mc.key.startsWith('avg_price_change_pct')) {
                          return <Td key={mc.key} className="text-center"><span className={signalColor(v)}>{formatValue(v, mc.key)}%</span></Td>;
                        }
                        return <Td key={mc.key} className="text-center">{formatValue(v, mc.key)}</Td>;
                      })}
                    </tr>
                  );
                });
              })}
            </tbody>
          </TableShell>
        </div>
      )}

      {model_pair_stats && Object.keys(model_pair_stats).length > 0 && (
        <div>
          <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Model Cross-Statistics</div>
          {Object.entries(model_pair_stats).map(([pairKey, pairData]) => {
            const parsed = parsePairKey(pairKey);
            const left = parsed?.left || 'model1';
            const right = parsed?.right || 'model2';
            return (
              <div key={pairKey} className="mb-4">
                <div className="text-[11px] font-medium mb-1 text-gray-600 dark:text-gray-300">
                  {modelLabel(left)} + {modelLabel(right)}
                </div>
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="text-center">{modelLabel(left)} Signal</Th>
                      <Th className="text-center">{modelLabel(right)} Signal</Th>
                      {metricColumns.map(c => <Th key={c.key} className="text-center">{c.label}</Th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(pairData || {}).flatMap(([leftSignalKey, rightMap]) =>
                      Object.entries(rightMap || {}).map(([rightSignalKey, cell]) => {
                        if (!cell) return null;
                        const leftSignal = String(leftSignalKey).replace(`${left}_`, '');
                        const rightSignal = String(rightSignalKey).replace(`${right}_`, '');
                        return (
                          <tr key={`${pairKey}-${leftSignalKey}-${rightSignalKey}`}>
                            <Td><span className={`font-semibold ${colorForSignal(leftSignal)}`}>{leftSignal}</span></Td>
                            <Td><span className={`font-semibold ${colorForSignal(rightSignal)}`}>{rightSignal}</span></Td>
                            {metricColumns.map(mc => {
                              const v = cell[mc.key];
                              if (mc.key.startsWith('avg_price_change_pct')) {
                                const nv = normalizeNumericLike(v);
                                return <Td key={mc.key} className="text-center"><span className={signalColor(nv)}>{formatValue(v, mc.key)}{typeof nv === 'number' ? '%' : ''}</span></Td>;
                              }
                              return <Td key={mc.key} className="text-center">{formatValue(v, mc.key)}</Td>;
                            })}
                          </tr>
                        );
                      }),
                    )}
                  </tbody>
                </TableShell>
              </div>
            );
          })}
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Note: Analysis based on up to {max_stats_bars_model} bars.</div>
        </div>
      )}
    </div>
  );
}

function TrendStats({ data }) {
  if (!data) return null;
  const { trending_score, bars_analyzed, criteria_values } = data || {};
  const scoreClass = trending_score > 0 ? 'text-green-600 dark:text-green-400' : trending_score < 0 ? 'text-red-600 dark:text-red-400' : '';
  const prettyCriteriaKeyMap = {
    trend_strength_adx: 'Trend Strength ADX',
    ma_alignment: 'MA Alignment',
    macd_momentum: 'MACD Momentum',
    oscillator_direction_rsi: 'Oscillator Direction RSI',
    volume_trend: 'Volume Trend',
    volatility_strength: 'Volatility Strength',
    divergence_continuation: 'Divergence Continuation',
  };
  const prettyCriteriaKey = (k) => prettyCriteriaKeyMap[k] || (k ? k.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') : '');
  return (
    <div className="space-y-3">
      <div className="text-sm text-left">
        Trending Score: {" "}
        <span className={`font-semibold ${scoreClass}`}>
          {typeof trending_score !== 'undefined' ? Number(trending_score).toFixed(2) : '-'}
        </span>
      </div>
      {criteria_values && (
        <div className="max-w-md w-full">
          <div className="text-[11px] font-semibold mb-1 uppercase text-gray-500 dark:text-gray-400">Criteria Values</div>
          <TableShell>
            <tbody>
              {Object.entries(criteria_values).map(([k, v]) => (
                <tr key={k}>
                  <Td className="whitespace-nowrap text-left">{prettyCriteriaKey(k)}</Td>
                  <Td className={typeof v === 'number' && v !== 0 ? (v > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400') : ''}>{typeof v === 'number' ? v.toFixed(3) : String(v)}</Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        </div>
      )}
      {typeof bars_analyzed !== 'undefined' && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400">Note: Analysis based on up to {bars_analyzed} bars.</div>
      )}
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  return (
    <details className={`group border rounded-md ${defaultOpen ? 'open' : ''} dark:border-gray-700`} open={defaultOpen}>
      <summary className="cursor-pointer select-none px-4 py-2 text-[14px] font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-between">
        <span>{title}</span>
        <svg
          className="w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ease-out group-open:rotate-90"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M7.22 14.78a.75.75 0 01-.073-.976l.073-.084L11.94 10 7.22 5.28a.75.75 0 01.976-1.133l.084.073 5 5a.75.75 0 01.073.976l-.073.084-5 5a.75.75 0 01-1.06 0z" clipRule="evenodd" />
        </svg>
      </summary>
      <div className="p-4">
        {children}
      </div>
    </details>
  );
}

export default function StockStatisticsAccordion({ stock }) {
  if (!stock) return null;
  const sections = [
    { key: 'div_stats', title: 'Divergence Statistics', render: () => <DivStats data={stock.div_stats} /> },
    { key: 'model_stats', title: 'Model Statistics', render: () => <ModelStats data={stock.model_stats} /> },
    { key: 'cons_stats', title: 'Consolidation Statistics', render: () => <ConsStats data={stock.cons_stats} /> },
    { key: 'trend_stats', title: 'Trend Statistics', render: () => <TrendStats data={stock.trend_stats} /> },
    { key: 'ensemble_stats', title: 'Ensemble Statistics', render: () => <EnsembleStats data={stock.ensemble_stats} /> },
  ];

  const hasAny = sections.some(s => {
    const val = stock[s.key];
    return typeof val !== 'undefined' && val !== null && (typeof val !== 'object' || Object.keys(val).length);
  });
  if (!hasAny) return null;

  return (
    <div className="space-y-2">
      {sections.map((s, idx) => {
        const val = stock[s.key];
        if (typeof val === 'undefined' || val === null || (typeof val === 'object' && !Object.keys(val).length)) return null;
        return (
          <Section key={s.key} title={s.title} defaultOpen={false}>
            {s.render()}
          </Section>
        );
      })}
    </div>
  );
}
